import { useState, useContext } from 'react';
import '../CompaniesPage/CompaniesPage.css';
import { DeleteOutlined } from '@ant-design/icons';
import { FinancialBuilderContext } from '../FinancialBuilderPage.jsx';
import { genericPOSTDatabaseCall, genericDELETEDatabaseCall } from '../../helperFunctions.jsx';

async function addFixedCostToDatabase(fixedCost) {
    return genericPOSTDatabaseCall('/api/fb/addFixedCost', { fixedCost }, 'fixed cost');
}

async function addFixedCostYearToDatabase(fixedCostYear) {
    return genericPOSTDatabaseCall('/api/fb/addFixedCostYear', { fixedCostYear }, 'fixed cost year');
}

async function deleteFixedCostFromDatabase(fixedCost) {
    return genericDELETEDatabaseCall('/api/fb/deleteFixedCost', { id: fixedCost.id, company_id: fixedCost.company_id }, 'fixed cost');
}

async function deleteFixedCostYearFromDatabase(fixedCostYear) {
    return genericDELETEDatabaseCall('/api/fb/deleteFixedCostYear', { id: fixedCostYear.id, fixed_cost_id: fixedCostYear.fixed_cost_id }, 'fixed cost year');
}

async function saveFixedCostToDatabase(fixedCost) {
    return genericPOSTDatabaseCall('/api/fb/saveFixedCost', { fixedCost }, 'fixed cost');
}

function RenderYears({years, setCompanySelected, messageApi}) {
    // Group years into chunks of 4
    const chunkedYears = [];
    for (let i = 0; i < years.length; i += 4) {
        chunkedYears.push(years.slice(i, i + 4));
    }

    const handleInputChange = (yearId, newValue) => {
        setCompanySelected((prev) => ({
            ...prev,
            fixed_costs: prev.fixed_costs.map(fc => ({
                ...fc,
                years: fc.years.map(y => y.id === yearId ? { ...y, amount: newValue } : y)
            }))
        }));
    };

    const handleInputBlur = (yearId, newValue) => {
        // Update the state with the new value for the specific year
        const regex = /^(\d{0,13}(\.\d{0,2})?)?$/;
        
        if (!regex.test(newValue)) {
            messageApi.error('Invalid input. Please enter a valid number. Max 13 digits before decimal and 2 after.');
            newValue = '0.00'; // Default value if invalid
        }
        if (newValue === '') {
            messageApi.error('This field cannot be empty');
            newValue = '0.00'; // Default value if empty
        }

        setCompanySelected((prev) => ({
            ...prev,
            fixed_costs: prev.fixed_costs.map(fc => ({
                ...fc,
                years: fc.years.map(y => y.id === yearId ? { ...y, amount: newValue } : y)
            }))
        }));
    };

    const deleteFixedCostYear = async (year) => {
        messageApi.loading('Deleting fixed cost year...', 0);
        try {
            await deleteFixedCostYearFromDatabase(year);
            setCompanySelected((prev) => ({
                ...prev,
                fixed_costs: prev.fixed_costs.map(fc => ({
                    ...fc,
                    years: fc.years.filter(y => y.id !== year.id)
                }))
            }));
            messageApi.destroy();
            messageApi.success('Fixed cost year deleted successfully');
        } catch (error) {
            console.error('Error deleting fixed cost year:', error);
            messageApi.destroy();
            messageApi.error('Failed to delete fixed cost year');
        }
    };

    return (
        <div>
            {chunkedYears.map((chunk, chunkIndex) => (
                <table key={chunkIndex} className="entity-table">
                    <thead>
                        <tr>
                            {chunk.map((year, index) => (
                                <th key={year.id}>Year {chunkIndex * 4 + index} <DeleteOutlined onClick={() => deleteFixedCostYear(year)} className="delete-year-icon" /></th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            {chunk.map((year) => (
                                <td key={year.id}>
                                    <input 
                                        className="form-input editable-cell" 
                                        type="text" 
                                        value={year.amount} 
                                        onChange={(e) => handleInputChange(year.id, e.target.value)}
                                        onBlur={(e) => handleInputBlur(year.id, e.target.value)}
                                    />
                                </td>
                            ))}
                        </tr>
                    </tbody>
                </table>
            ))}
        </div>
    );
}

function FixedCostsDisplay({ fixedCost, setCompanySelected, messageApi }) {
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const saveFixedCost = async (updatedFixedCost) => {
        setSaving(true);
        messageApi.loading('Saving fixed cost...', 0);
        try {
            await saveFixedCostToDatabase(updatedFixedCost);
            messageApi.destroy();
            messageApi.success('Fixed cost saved successfully');
        } catch (error) {
            console.error('Error saving fixed cost:', error);
            messageApi.destroy();
            messageApi.error('Failed to save fixed cost');
        }
        setSaving(false);
    }

    const deleteFixedCost = async () => {
        setDeleting(true);
        messageApi.loading('Deleting fixed cost...', 0);
        try {
            await deleteFixedCostFromDatabase(fixedCost);
            setCompanySelected((prev) => ({
                ...prev,
                fixed_costs: prev.fixed_costs.filter(fc => fc.id !== fixedCost.id)
            }));
            messageApi.destroy();
            messageApi.success('Fixed cost deleted successfully');
        } catch (error) {
            console.error('Error deleting fixed cost:', error);
            messageApi.destroy();
            messageApi.error('Failed to delete fixed cost');
        }
    }

    const addFixedCostYear = async (fixedCostId) => {
        // Logic to add a new year to the fixed cost
        messageApi.info('Adding new year to fixed cost...');
        try {
            const newYearData = await addFixedCostYearToDatabase({ fixed_cost_id: fixedCostId });
            setCompanySelected((prev) => ({
                ...prev,
                fixed_costs: prev.fixed_costs.map(fc => 
                    fc.id === fixedCostId ? { ...fc, years: [...fc.years, newYearData] } : fc
                )
            }));
            messageApi.destroy();
            messageApi.success('New year added successfully');
        } catch (error) {
            console.error('Error adding new year:', error);
            messageApi.destroy();
            messageApi.error('Failed to add new year');
        }
    }

    const changeFixedCostName = (event) => {
        const newName = event.target.value;
        setCompanySelected((prev) => ({
            ...prev,
            fixed_costs: prev.fixed_costs.map(fc => 
                fc.id === fixedCost.id ? { ...fc, name: newName } : fc
            )
        }));
    }

    return (
        <div className="entity-card" key={fixedCost.id}>
            <div className="form-row">
                <label htmlFor={"fixed_cost_name_" + fixedCost.id}>Fixed Cost Name:</label>
                <input id={"fixed_cost_name_" + fixedCost.id} className="form-input" type="text" value={fixedCost.name} onChange={(event) => changeFixedCostName(event)} />
            </div>
            <RenderYears years={fixedCost.years} setCompanySelected={setCompanySelected} messageApi={messageApi} />
            <div className="flex-row bottom-actions">
                <div className="form-row actions-row" style={{ flex: 1 }}>
                    <button type='button' onClick={() => addFixedCostYear(fixedCost.id)} className='add-button'>Add Year</button>
                </div>
            </div>
            <div className="flex-row bottom-actions">
                <div className="form-row actions-row">
                    <button type='button' disabled={deleting} onClick={() => deleteFixedCost(fixedCost)} className="delete-button">Delete Fixed Cost</button>
                </div>
                <div className="form-row actions-row">
                    <button type='button' disabled={saving} onClick={() => saveFixedCost(fixedCost)} className="save-button">Save Fixed Cost</button>
                </div>
            </div>
        </div>
    )
}

function FixedCostsPage({showNavigationControls = true, contextToUse }) {
    const { setPageNum, companySelected, setCompanySelected, messageApi } = useContext(contextToUse || FinancialBuilderContext);
    const [fixedCostName, setFixedCostName] = useState('');

    const addNewFixedCost = async () => {
        if (!fixedCostName) {
            messageApi.error('Please enter a fixed cost name');
            return;
        }
        messageApi.loading('Adding new fixed cost...');
        try {
            const newFixedCost = await addFixedCostToDatabase({ name: fixedCostName, company_id: companySelected.fb_id !== undefined ? companySelected.fb_id : companySelected.id });
            setCompanySelected((prev) => ({
                ...prev,
                fixed_costs: [...prev.fixed_costs, newFixedCost]
            }));
            setFixedCostName(''); // Clear input field after adding
            messageApi.destroy();
            messageApi.success('Fixed cost added successfully');
        } catch (error) {
            console.error('Error adding fixed cost:', error);
            messageApi.destroy();
            messageApi.error('Failed to add fixed cost');
        }
    }

    return (
        <div className="financial-builder-container entity-industry-container fixed-costs-container">
            <h2>{companySelected.name}- Fixed Costs ({companySelected.fixed_costs.length})</h2>
            <div className="product-list">
                <h3>Existing Fixed Costs</h3>
                <div>
                    {companySelected.fixed_costs.map((fixedCost) => (
                        <FixedCostsDisplay key={fixedCost.id} fixedCost={fixedCost} setCompanySelected={setCompanySelected} messageApi={messageApi} />
                    ))}
                </div>
            </div>
            <div className="add-entity-controls form-section">
                <div className="form-row">
                    <label htmlFor="new_fixed_cost_name">Add New Fixed Cost</label>
                    <input type="text" className="form-input" value={fixedCostName} onChange={(e) => setFixedCostName(e.target.value)} placeholder="Enter fixed cost name" id="new_fixed_cost_name" />
                </div>
                <button onClick={addNewFixedCost} className="add-button">ADD FIXED COST</button>
            </div>
            {!showNavigationControls ? <></> : (
                <div className="navigation-button-container">
                <button type="button" onClick={() => setPageNum(2)} className="next-button">Back</button>
                <button type="button" onClick={() => setPageNum(4)} className="next-button">Next</button>
                </div>
            )}
        </div>
    )
}

export default FixedCostsPage;